import React, { use, useCallback, useEffect, useMemo, useState } from "react";
import { StackScreenProps } from "@react-navigation/stack";
import commonStyles from "../common-style";
import { useI18n } from "../../hooks/i18n-hooks";
import { useTheme } from "../../hooks/use-theme";
import VStack from "../../ui_atoms/VStack";
import CodeField from "../../components/CodeField";
import Loading from "../../components/Loading";
import { BodyMediumText } from "../../components/FormattedText";
import Text from "../../components/Text";
import { AuthManagerContext } from "../../contexts/AuthManager";
import { useAtomValue } from "jotai";
import { phoneNumberConfirmResult } from "../../atoms/auth";
import { SignupSigninStackParamList } from "../../navigations/SignupSigninStack";
import { FirebaseManagerContext } from "../../contexts/FirebaseManager";
import { useTracking } from "../../hooks/tracking";
import { captureFirebaseAuthException } from "../../utils";
import ScreenCard from "../ScreenCard";

export const CELL_COUNT = 6;

type MessageInfo = {
    text: string;
    color?: string;
};

type ConfirmScreenProps = StackScreenProps<SignupSigninStackParamList, "ConfirmCode">;

const ConfirmationScreen = ({ navigation, route }: ConfirmScreenProps) => {
    const i18n = useI18n();
    const { colors } = useTheme();
    const { trackEvent } = useTracking();
    const { phoneNumber, isSignup = false } = route.params;
    const [message, setMessage] = useState<MessageInfo>();
    const [isProcessing, setIsProcessing] = useState(false);
    const [value, setValue] = useState("");
    const { user } = use(AuthManagerContext);
    const phoneNumberConfirmResultValue = useAtomValue(phoneNumberConfirmResult);
    const { user: firebaseUser } = use(FirebaseManagerContext);

    const isValid = useMemo(() => {
        return !!phoneNumberConfirmResultValue && value.length >= CELL_COUNT;
    }, [value, phoneNumberConfirmResultValue]);

    const isLoading = isProcessing;

    useEffect(() => {
        void trackEvent("ask_activation_code_showed");
    }, [trackEvent, isSignup]);

    const validate = useCallback(async () => {
        try {
            setMessage(undefined);
            setIsProcessing(true);

            await phoneNumberConfirmResultValue?.confirm(value);
            // signin will change the auth user
            // if user exists and is a worker the root navigations controller that will transition to the User stack
            // in all other cases, a hook will do the navigation to next screen
        } catch (err) {
            captureFirebaseAuthException(err);
            setMessage({
                text: i18n.t("screens.auth.confirmation_code.wrong_code"),
                color: "red",
            });
            setIsProcessing(false);
        }
    }, [setIsProcessing, phoneNumberConfirmResultValue, value, i18n]);

    useEffect(() => {
        // once user === null it means we have login to firebase (user has entered its code) and don't have an account yet
        if (firebaseUser && user === null) {
            void trackEvent("signup_started");
            navigation.replace("SelectRole", { isSignup });
        }
    }, [firebaseUser, user, navigation, isSignup, trackEvent]);

    useEffect(() => {
        if (isValid) {
            void validate();
        }
    }, [isValid, validate]);

    return (
        <ScreenCard>
            <VStack gap={1} v={0} h="2/3">
                <BodyMediumText>
                    {i18n.t("screens.auth.confirmation_code.description", {
                        phoneNumber,
                    })}
                </BodyMediumText>
                <CodeField
                    value={value}
                    setValue={setValue}
                    cellCount={6}
                    autoFocus
                    cellStyle={isLoading ? { backgroundColor: colors.backdrop } : {}}
                />
                {isLoading && <Loading />}
                <Text
                    style={[
                        commonStyles.screenErrorMessage,
                        { color: message ? message.color : "transparent" },
                    ]}
                >
                    {message?.text}
                </Text>
            </VStack>
        </ScreenCard>
    );
};

export default ConfirmationScreen;
